import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { safeSelectValue } from '../../lib/safeSelectValue';
import { TERRITOIRES, PAYS_OPTIONS, SPECIALITES } from './constants';

const API = process.env.REACT_APP_BACKEND_URL;

const INITIAL_COOPER_FORM = {
  nom: '', prenom: '', email: '', password: '', telephone: '',
  entreprise: '', siret: '', adresse: '', ville: '', code_postal: '',
  pays: 'FR', indicatif: '+33', territoire: '', zone_activite: []
};

const INITIAL_EXPERT_FORM = {
  nom: '', prenom: '', email: '', password: '', telephone: '',
  specialite: '', numero_agrement: '', zones_intervention: [],
  coopers_id: ''
};

export default function useCoopersExperts({ getAuthHeader }) {
  const [coopers, setCoopers] = useState([]);
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Modals
  const [showCreateCooperModal, setShowCreateCooperModal] = useState(false);
  const [showCreateExpertModal, setShowCreateExpertModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editMode, setEditMode] = useState(null);

  // Password visibility
  const [showCooperPassword, setShowCooperPassword] = useState(false);
  const [showExpertPassword, setShowExpertPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Invitation dialog
  const [showInvitationDialog, setShowInvitationDialog] = useState(false);
  const [invitationRole, setInvitationRole] = useState(null);

  // Forms
  const [resetPasswordForm, setResetPasswordForm] = useState({ newPassword: '', confirmPassword: '' });
  const [cooperForm, setCooperForm] = useState(INITIAL_COOPER_FORM);
  const [expertForm, setExpertForm] = useState(INITIAL_EXPERT_FORM);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [coopersRes, expertsRes] = await Promise.all([
        axios.get(`${API}/api/coopers`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/experts`, { headers: getAuthHeader() })
      ]);
      const sortByDate = (arr) => (arr || []).sort((a, b) => new Date(b.date_creation || b.created_at || 0) - new Date(a.date_creation || a.created_at || 0));
      setCoopers(sortByDate(coopersRes.data?.coopers || coopersRes.data || []));
      setExperts(sortByDate(expertsRes.data?.experts || expertsRes.data || []));
    } catch {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { loadData(); }, [loadData]);

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) password += chars.charAt(Math.floor(Math.random() * chars.length));
    return password;
  };

  const openResetPasswordModal = (item, type) => {
    setSelectedItem(item);
    setEditMode(type);
    setResetPasswordForm({ newPassword: '', confirmPassword: '' });
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setShowResetPasswordModal(true);
  };

  const handleResetPassword = async () => {
    if (!resetPasswordForm.newPassword) { toast.error('Veuillez entrer un nouveau mot de passe'); return; }
    if (resetPasswordForm.newPassword !== resetPasswordForm.confirmPassword) { toast.error('Les mots de passe ne correspondent pas'); return; }
    if (resetPasswordForm.newPassword.length < 8) { toast.error('Le mot de passe doit contenir au moins 8 caractères'); return; }
    try {
      const endpoint = editMode === 'cooper'
        ? `${API}/api/coopers/${selectedItem.id}/reset-password`
        : `${API}/api/experts/${selectedItem.id}/reset-password`;
      await axios.put(endpoint, { password: resetPasswordForm.newPassword }, { headers: getAuthHeader() });
      toast.success('Mot de passe réinitialisé avec succès');
      setShowResetPasswordModal(false);
      setResetPasswordForm({ newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    }
  };

  const stats = useMemo(() => {
    const coopersActifs = coopers.filter(c => c.statut === 'actif' || c.is_validated).length;
    const coopersSuspendus = coopers.filter(c => c.statut === 'suspendu').length;
    const expertsActifs = experts.filter(e => e.statut === 'actif').length;
    const expertsSuspendus = experts.filter(e => e.statut === 'suspendu').length;
    const totalMembres = coopers.reduce((sum, c) => sum + (c.membres_count || 0), 0);
    return { totalCoopers: coopers.length, coopersActifs, coopersSuspendus, totalExperts: experts.length, expertsActifs, expertsSuspendus, totalMembres };
  }, [coopers, experts]);

  const filteredCoopers = useMemo(() => coopers.filter(c => {
    const matchSearch = !searchTerm || [c.nom, c.prenom, c.email, c.entreprise].some(f => (f || '').toLowerCase().includes(searchTerm.toLowerCase()));
    const matchStatus = filterStatus === 'all' || c.statut === filterStatus || (filterStatus === 'actif' && c.is_validated && !c.statut);
    return matchSearch && matchStatus;
  }), [coopers, searchTerm, filterStatus]);

  const filteredExperts = useMemo(() => experts.filter(e => {
    const matchSearch = !searchTerm || [e.nom, e.prenom, e.email, e.specialite].some(f => (f || '').toLowerCase().includes(searchTerm.toLowerCase()));
    const matchStatus = filterStatus === 'all' || e.statut === filterStatus;
    return matchSearch && matchStatus;
  }), [experts, searchTerm, filterStatus]);

  // CRUD Cooper
  const createCooper = async () => {
    if (!cooperForm.email || !cooperForm.nom) { toast.error('Email et nom requis'); return; }
    try {
      await axios.post(`${API}/api/coopers`, cooperForm, { headers: getAuthHeader() });
      toast.success("COOPER'S créé avec succès");
      setShowCreateCooperModal(false);
      setCooperForm(INITIAL_COOPER_FORM);
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de la création'); }
  };

  const updateCooperStatus = async (cooperId, newStatus) => {
    try {
      await axios.put(`${API}/api/coopers/${cooperId}/status`, { statut: newStatus }, { headers: getAuthHeader() });
      toast.success(`COOPER'S ${newStatus === 'actif' ? 'activé' : newStatus === 'suspendu' ? 'suspendu' : 'désactivé'}`);
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour'); }
  };

  const validateCooper = async (cooperId) => {
    try {
      await axios.put(`${API}/api/coopers/${cooperId}/validate`, {}, { headers: getAuthHeader() });
      toast.success("COOPER'S validé avec succès");
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de la validation'); }
  };

  const deleteCooper = async (cooperId) => {
    if (!window.confirm("Supprimer définitivement ce COOPER'S ?")) return;
    try {
      await axios.delete(`${API}/api/coopers/${cooperId}`, { headers: getAuthHeader() });
      toast.success("COOPER'S supprimé");
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de la suppression'); }
  };

  // CRUD Expert
  const createExpert = async () => {
    if (!expertForm.email || !expertForm.nom) { toast.error('Email et nom requis'); return; }
    try {
      await axios.post(`${API}/api/experts`, expertForm, { headers: getAuthHeader() });
      toast.success('Expert créé avec succès');
      setShowCreateExpertModal(false);
      setExpertForm(INITIAL_EXPERT_FORM);
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de la création'); }
  };

  const updateExpertStatus = async (expertId, newStatus) => {
    try {
      await axios.put(`${API}/api/experts/${expertId}/status`, { statut: newStatus }, { headers: getAuthHeader() });
      toast.success(`Expert ${newStatus === 'actif' ? 'activé' : newStatus === 'suspendu' ? 'suspendu' : 'désactivé'}`);
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour'); }
  };

  const deleteExpert = async (expertId) => {
    if (!window.confirm('Supprimer définitivement cet expert ?')) return;
    try {
      await axios.delete(`${API}/api/experts/${expertId}`, { headers: getAuthHeader() });
      toast.success('Expert supprimé');
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de la suppression'); }
  };

  // Edit
  const openEditCooper = (cooper) => {
    const territoireIds = TERRITOIRES.map(t => t.id);
    const paysCodes = PAYS_OPTIONS.map(p => p.code);
    setSelectedItem(cooper);
    setCooperForm({
      nom: cooper.nom || '', prenom: cooper.prenom || '', email: cooper.email || '', password: '',
      telephone: cooper.telephone || '', entreprise: cooper.entreprise || '', siret: cooper.siret || '',
      adresse: cooper.adresse || '', ville: cooper.ville || '', code_postal: cooper.code_postal || '',
      pays: safeSelectValue(cooper.pays, paysCodes, 'FR'),
      indicatif: cooper.indicatif || '+33',
      territoire: safeSelectValue(cooper.territoire, territoireIds, ''),
      zone_activite: cooper.zone_activite || [],
    });
    setEditMode('cooper');
    setShowEditModal(true);
  };

  const openEditExpert = (expert) => {
    setSelectedItem(expert);
    setExpertForm({
      nom: expert.nom || '', prenom: expert.prenom || '', email: expert.email || '', password: '',
      telephone: expert.telephone || '',
      specialite: safeSelectValue(expert.specialite, SPECIALITES, ''),
      numero_agrement: expert.numero_agrement || '', zones_intervention: expert.zones_intervention || [],
      coopers_id: expert.coopers_id || ''
    });
    setEditMode('expert');
    setShowEditModal(true);
  };

  const saveEdit = async () => {
    try {
      if (editMode === 'cooper') {
        await axios.put(`${API}/api/coopers/${selectedItem.id}`, cooperForm, { headers: getAuthHeader() });
        toast.success("COOPER'S mis à jour");
      } else {
        await axios.put(`${API}/api/experts/${selectedItem.id}`, expertForm, { headers: getAuthHeader() });
        toast.success('Expert mis à jour');
      }
      setShowEditModal(false);
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour'); }
  };

  return {
    coopers, experts, loading, searchTerm, setSearchTerm, filterStatus, setFilterStatus,
    stats, filteredCoopers, filteredExperts,
    // Modals
    showCreateCooperModal, setShowCreateCooperModal, showCreateExpertModal, setShowCreateExpertModal,
    showEditModal, setShowEditModal, showDetailModal, setShowDetailModal,
    showResetPasswordModal, setShowResetPasswordModal, selectedItem, setSelectedItem, editMode,
    showCooperPassword, setShowCooperPassword, showExpertPassword, setShowExpertPassword,
    showNewPassword, setShowNewPassword, showConfirmPassword, setShowConfirmPassword,
    showInvitationDialog, setShowInvitationDialog, invitationRole, setInvitationRole,
    // Forms
    cooperForm, setCooperForm, expertForm, setExpertForm, resetPasswordForm, setResetPasswordForm,
    // Actions
    loadData, generatePassword, openResetPasswordModal, handleResetPassword,
    createCooper, updateCooperStatus, validateCooper, deleteCooper,
    createExpert, updateExpertStatus, deleteExpert,
    openEditCooper, openEditExpert, saveEdit,
  };
}
